// IIFE : voir cart-offer.js. Les deux fichiers sont des scripts classiques et partagent la portée
// globale ; `isFullCart` y était déclaré deux fois, ce qui tuait le second au parsing.
(() => {
// Dernier panier connu, partagé par toutes les instances. Les deux surfaces (tiroir, page panier)
// sont détruites et reconstruites par la Section Rendering API à chaque mutation du panier, donc
// une instance fraîchement clonée doit pouvoir rejouer l'état courant au lieu d'afficher celui du
// chargement de page.
let lastCartData;
let cartFetch;

// `cartUpdate` transporte selon l'émetteur soit le panier complet (/cart/change.js,
// /cart/update.js, /cart.js), soit la ou les lignes ajoutées (/cart/add.js, publié tel quel par
// product-form.js et variant-selector-layer.js). Seul le panier complet porte `item_count` :
// prendre une réponse d'ajout pour un panier ferait chuter le sous-total et réinitialiserait
// la barre à chaque ajout.
const isFullCart = (cart) => Boolean(cart) && cart.item_count !== undefined && Array.isArray(cart.items);

function resolveCart(cartData) {
  if (isFullCart(cartData)) return Promise.resolve(cartData);

  // Une seule requête partagée par toutes les instances (tiroir + copie de la page panier).
  if (!cartFetch) {
    cartFetch = fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .finally(() => {
        cartFetch = null;
      });
  }

  return cartFetch;
}

// Rendu de TOUTES les instances vivantes, et non de celle qui a reçu l'événement : product-form.js
// publie `cartUpdate` AVANT d'appeler renderContents, donc l'instance clonée ensuite n'aurait
// jamais vu la mise à jour et resterait sur l'état du chargement de page (le F5 nécessaire après
// un ajout depuis le panier vide ou un cross-sell).
function renderAllTiers() {
  document.querySelectorAll('cart-tiers').forEach((element) => element.render(lastCartData));
}

// Un seul abonnement pour toute la page : chaque instance est ensuite servie par renderAllTiers.
subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
  try {
    const cart = await resolveCart(event.cartData);
    if (!isFullCart(cart)) return;

    lastCartData = cart;
    renderAllTiers();
  } catch (error) {
    console.error('[Cart Tiers] Failed to resolve cart:', error);
  }
});

class CartTiers extends HTMLElement {
  connectedCallback() {
    const configElement = this.querySelector('[data-cart-tiers-config]');
    if (!configElement) return;

    try {
      this.config = JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('[Cart Tiers] Failed to parse config:', error);
      return;
    }

    const brokenTiers = Number(this.dataset.brokenTiers);
    if (brokenTiers > 0) {
      console.warn(
        `[Cart Tiers] ${brokenTiers} palier(s) cadeau ignoré(s) : produit offert introuvable, sans variante, ou montant à 0.`
      );
    }

    // Le markup cloné porte l'état du chargement de page : rejouer le dernier panier connu.
    if (lastCartData) this.render(lastCartData);
  }

  render(cartData) {
    const items = cartData?.items;
    if (!items || !this.config) return;

    const { tiers, maxThresholdCents, strings } = this.config;
    if (!maxThresholdCents) return;

    this.hidden = items.length === 0;

    // Même définition que cart-tiers.liquid et cart-offer.js : total après remises, moins TOUTES
    // les lignes cadeau, sinon le cadeau d'un palier gonfle le sous-total du palier suivant.
    // Hors don aussi (LP-20).
    const subtotal = items.reduce(
      (sum, item) =>
        item.properties?._offer_gift === 'true' || item.properties?._donation === 'true'
          ? sum - (item.final_line_price || 0)
          : sum,
      cartData.total_price || 0
    );

    this.style.setProperty('--cart-tiers-progress', Math.min(subtotal / maxThresholdCents, 1));

    this.querySelectorAll('[data-cart-tiers-mark]').forEach((mark) => {
      const reached = subtotal >= Number(mark.dataset.cartTiersMark);
      mark.classList.toggle('cart-tiers__mark--reached', reached);

      const state = mark.querySelector('[data-cart-tiers-mark-state]');
      if (state) state.textContent = reached ? strings.reached : strings.notReached;
    });

    const nextTier = tiers
      .filter((tier) => subtotal < tier.thresholdCents)
      .sort((left, right) => left.thresholdCents - right.thresholdCents)[0];

    const message = this.querySelector('[data-cart-tiers-message]');
    if (!message) return;

    message.innerHTML = nextTier
      ? strings.remaining
          .replace('[amount]', this.#money(nextTier.thresholdCents - subtotal))
          .replace('[reward]', nextTier.reward)
      : strings.allReached;
  }

  // ponytail: Intl plutôt que shop.money_format ; à remplacer par un formateur dérivé de
  // money_format si le marchand personnalise le format monétaire de la boutique.
  #money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'fr', {
      style: 'currency',
      currency: this.config.currency,
    }).format(cents / 100);
  }
}

if (!customElements.get('cart-tiers')) {
  customElements.define('cart-tiers', CartTiers);
}

// La barre est rendue une seule fois par la section du tiroir, dans #CartTiersTemplate, hors de la
// zone que la Section Rendering API remplace — c'est le seul rendu où les blocs de la section sont
// résolus. Chaque surface la clone ici, y compris après un re-rendu du tiroir ou de main-cart-items.
class CartTiersMount extends HTMLElement {
  connectedCallback() {
    if (this.firstElementChild) return;

    const template = document.getElementById('CartTiersTemplate');
    if (template) this.append(template.content.cloneNode(true));
  }
}

if (!customElements.get('cart-tiers-mount')) {
  customElements.define('cart-tiers-mount', CartTiersMount);
}
})();
